import { classNamesFunction, ITextProps, ITextStyles } from '@fluentui/react';
import { Image } from '@fluentui/react/lib/Image';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';

const messages = defineMessages({
    sentenceFiller: { id: 'invalidRegionTablePlaceholder.sentenceFiller', defaultMessage: 'for this feature and' },
    regionAvailability: { id: 'invalidRegionTablePlaceholder.regionAvailability', defaultMessage: 'See region availability' },
    selectAnotherResource: {
        id: 'invalidRegionTablePlaceholder.selectAnotherResource',
        defaultMessage: 'select another resource to proceed with project creation.'
    },
    notValidResourceRegion: {
        id: 'invalidRegionTablePlaceholder.notValidResourceRegion',
        defaultMessage: `{featureName} is only available in certain regions. The selected resource's region
        does not match the regions supported for this feature.`
    },
    notValidResourceKind: {
        id: 'invalidRegionTablePlaceholder.notValidResourceKind',
        defaultMessage: `{featureName} is only available using a Language resource type.`
    },
    somethingWentWrongImageAltText: {
        id: 'invalidRegionTablePlaceholder.somethingWentWrongImageAltText',
        defaultMessage: 'Something went wrong image.'
    }
});

export type ResourceUnSupportedErrorType = 'unsupportedRegion' | 'unsupportedResource';

export type FeatureUnavailableInRegionProps = {
    docsLink: string;
    featureName: string;
    errorType: ResourceUnSupportedErrorType;
};

const textStyles = classNamesFunction<ITextProps, ITextStyles>()({ root: { maxWidth: '800px', textAlign: 'center' } });

/**
 * Renders a component that shows the required message with the included links
 * when the selected resource is invalid because the feature is not supported
 * in its region
 */
export const FeatureUnavailableInRegion = (props: FeatureUnavailableInRegionProps) => {
    const { featureName, docsLink, errorType } = props;

    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();

    return (
        <Stack verticalFill verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: 42 }}>
            <Image
                alt={localize(messages.somethingWentWrongImageAltText)}
                src={getAssetsUrl('assets/images/languageStudio/somethingWentWrongLogo.svg')}
            />

            {errorType === 'unsupportedRegion' ? (
                <LuisText styles={textStyles}>
                    {localize(messages.notValidResourceRegion, { featureName })}
                    &nbsp;
                    <Link href={docsLink} target="_blank">
                        {localize(messages.regionAvailability)}
                    </Link>
                    &nbsp;
                    {localize(messages.sentenceFiller)}
                    &nbsp;
                    <Link onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                        {localize(messages.selectAnotherResource)}
                    </Link>
                </LuisText>
            ) : (
                <LuisText styles={textStyles}>
                    {localize(messages.notValidResourceKind, { featureName })}
                    &nbsp;
                    <Link onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}>
                        {localize(messages.selectAnotherResource)}
                    </Link>
                </LuisText>
            )}
        </Stack>
    );
};
